import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, IconButton, Tooltip, Typography, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { SuperAdminDataTable, type AdminColumn } from '../superadmin/SuperAdminDataTable';
import { DocumentUploadDialog } from './DocumentUploadDialog';
import { DriverDocumentsPackDialog } from './DriverDocumentsPackDialog';
import { documentAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import {
  type DocumentRecord,
  type EntityType,
  getDocumentTypeLabel,
  getExpiryStatusColor,
  getVerificationStatusColor,
  EXPIRY_STATUS_OPTIONS,
  VERIFICATION_STATUS_OPTIONS,
} from '../../constants/documentTypes';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';

interface DocumentPanelProps {
  entityType: EntityType;
  entityId: string;
  entityLabel?: string;
  title?: string;
  compact?: boolean;
}

export const DocumentPanel: React.FC<DocumentPanelProps> = ({
  entityType,
  entityId,
  entityLabel,
  title = 'Documents',
  compact = false,
}) => {
  const { hasPermission } = useAuth();
  const { formatDate } = useLocale();
  const [rows, setRows] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [packOpen, setPackOpen] = useState(false);
  const [editDoc, setEditDoc] = useState<DocumentRecord | null>(null);

  const canCreate = hasPermission('documents', 'create');
  const canEdit = hasPermission('documents', 'edit');
  const canDelete = hasPermission('documents', 'delete');
  const canApprove = hasPermission('documents', 'approve');

  const load = useCallback(async () => {
    if (!entityId) return;
    try {
      setLoading(true);
      setError('');
      const res = await documentAPI.list({ entityType, entityId });
      setRows(res.data || []);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const handleView = async (doc: DocumentRecord) => {
    try {
      const blob = await documentAPI.downloadFile(doc._id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setError('Failed to open document');
    }
  };

  const handleDelete = async (doc: DocumentRecord) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await documentAPI.remove(doc._id);
      load();
    } catch {
      setError('Delete failed');
    }
  };

  const handleVerify = async (doc: DocumentRecord, status: 'approved' | 'rejected') => {
    const remarks = status === 'rejected'
      ? window.prompt('Rejection remarks (optional):') || ''
      : '';
    try {
      await documentAPI.verify(doc._id, { verificationStatus: status, remarks });
      load();
    } catch {
      setError('Verification update failed');
    }
  };

  const columns: AdminColumn<DocumentRecord>[] = [
    {
      id: 'documentType',
      header: 'Type',
      render: (d) => getDocumentTypeLabel(entityType, d.documentType),
      searchValue: (d) => getDocumentTypeLabel(entityType, d.documentType),
      exportValue: (d) => d.documentType,
      filterType: 'text',
    },
    {
      id: 'issueDate',
      header: 'Issue',
      render: (d) => (d.issueDate ? formatDate(d.issueDate) : '—'),
      searchValue: (d) => d.issueDate || '',
      sortValue: (d) => d.issueDate,
    },
    {
      id: 'expiryDate',
      header: 'Expiry',
      render: (d) => (d.expiryDate ? formatDate(d.expiryDate) : '—'),
      searchValue: (d) => d.expiryDate || '',
      sortValue: (d) => d.expiryDate,
    },
    {
      id: 'expiryStatus',
      header: 'Expiry Status',
      render: (d) => {
        const c = getExpiryStatusColor(d.expiryStatus);
        const label = d.expiryStatus === 'expiring_7'
          ? 'Expiring 7 days'
          : d.expiryStatus === 'expiring_30' || d.expiryStatus === 'expiring_soon'
            ? 'Expiring 30 days'
            : d.expiryStatus === 'active' ? 'Valid' : d.expiryStatus.replace(/_/g, ' ');
        return (
          <Chip label={label} size="small"
            sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, textTransform: 'capitalize' }} />
        );
      },
      filterOptions: EXPIRY_STATUS_OPTIONS,
      filterType: 'select',
      filterValue: (d) => d.expiryStatus,
    },
    {
      id: 'verificationStatus',
      header: 'Verification',
      render: (d) => {
        const c = getVerificationStatusColor(d.verificationStatus);
        return (
          <Chip label={d.verificationStatus} size="small"
            sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, textTransform: 'capitalize' }} />
        );
      },
      filterOptions: VERIFICATION_STATUS_OPTIONS,
      filterType: 'select',
      filterValue: (d) => d.verificationStatus,
    },
    {
      id: 'fileName',
      header: 'File',
      render: (d) => (
        <Typography fontSize={13} noWrap sx={{ maxWidth: 160 }} title={d.fileName}>
          {d.fileName || '—'}
        </Typography>
      ),
      searchValue: (d) => d.fileName || '',
      filterType: 'text',
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography fontWeight={700} fontSize={compact ? 15 : 18}>{title}</Typography>
        {canCreate && (
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => {
              if (entityType === 'driver' || entityType === 'helper') {
                setPackOpen(true);
              } else {
                setEditDoc(null);
                setDialogOpen(true);
              }
            }}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: `${RADIUS}px`, background: brandCss.gradientPrimary }}>
            {entityType === 'driver' || entityType === 'helper' ? 'Upload documents' : 'Upload Document'}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <SuperAdminDataTable
        columns={columns as unknown as AdminColumn<Record<string, unknown>>[]}
        rows={rows as unknown as Record<string, unknown>[]}
        loading={loading}
        error={error && !rows.length ? error : undefined}
        searchPlaceholder="Search documents..."
        emptyMessage="No documents uploaded yet"
        recordLabel="documents"
        enableExport={false}
        statusField=""
        statusOptions={[]}
        renderActions={(row) => {
          const d = row as unknown as DocumentRecord;
          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
              <Tooltip title="View file">
                <IconButton size="small" onClick={() => handleView(d)} color="primary">
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canEdit && (
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => { setEditDoc(d); setDialogOpen(true); }} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canApprove && d.verificationStatus === 'pending' && (
                <>
                  <Tooltip title="Approve">
                    <IconButton size="small" onClick={() => handleVerify(d, 'approved')} sx={{ color: brandCss.primary }}>
                      <CheckCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reject">
                    <IconButton size="small" onClick={() => handleVerify(d, 'rejected')} color="error">
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {canDelete && (
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDelete(d)} color="error">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        }}
      />

      <DocumentUploadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
        entityType={entityType}
        entityId={entityId}
        entityLabel={entityLabel}
        editDoc={editDoc}
      />

      {(entityType === 'driver' || entityType === 'helper') && (
        <DriverDocumentsPackDialog
          open={packOpen}
          onClose={() => setPackOpen(false)}
          onSaved={load}
          entityId={entityId}
          entityLabel={entityLabel}
          entityType={entityType}
        />
      )}
    </Box>
  );
};
